/**
 * Work transcript projection.
 *
 * One pure function decides the row list of a Work chat:
 *
 *   rows = server rows (Conversation.messages, seq <= eventsSyncedSeq)
 *        + rows projected from transcript events with seq > eventsSyncedSeq
 *          (same rules as backend buildMessagesFromEvents)
 *        + the live assistant draft (`cw-<task>-streaming`)
 *        + pending sends (`msg_<clientId>`) until their user_message lands
 *        + queued follow-ups
 *
 * Ids are deterministic (`cw-<task>-<seq>`), so a row never changes key when
 * the server projection catches up with the client-side one. There is no
 * text-matching merge and nothing paints into the chat store.
 */
import {
  stripWorkInjectedPrompt,
  workArtifactEntryFromEvent,
  workArtifactHolderId,
  workOfficialMessageId,
  workSeqFromMessageId,
  workStreamingMessageId,
  isWorkArtifactHolderId,
  WORK_QUESTION_MAX_CHARS,
  slimWorkMediaReview,
} from '@/constants/work';
import { isRedundantPreviousStill, previousStillSizeIndex } from '@/components/Coworker/coworkerArtifactTypes';
import { isImageOutputFile } from '@/constants/utils/message';

export const WORK_TRANSCRIPT_EVENT_TYPES = new Set([
  'user_message',
  'message_end',
  'user_question_requested',
  'user_question_resolved',
  'artifact_ready',
  'session_start',
  'session_resume',
  'session_end',
]);

const USER_TEXT_MAX = 20000;
const ASSISTANT_TEXT_MAX = 100000;
const THINKING_MAX = 20000;

function cap(value, max) {
  const text = String(value || '');
  return text.length > max ? text.slice(0, max) : text;
}

/**
 * Work uploads render inline from `images` on the server-side message shape
 * (MessageBubble draws the thumbnails from message.images). The live +
 * pending user rows must mirror that split so an uploaded picture appears
 * above the message immediately instead of only after a history refetch
 * returns the stored Conversation.messages row.
 */
function splitWorkUploadFiles(files, imageFiles) {
  const all = (Array.isArray(files) ? files : []).filter(file => file && typeof file === 'object');
  const images = [];
  const seen = new Set();
  for (const file of [...(Array.isArray(imageFiles) ? imageFiles : []), ...all.filter(isImageOutputFile)]) {
    if (!file || typeof file !== 'object') continue;
    const key = file.id || file._id || file.url || file.cdnPath || file.uri || file;
    if (seen.has(key)) continue;
    seen.add(key);
    images.push(file);
  }
  return {
    files: all.filter(file => !isImageOutputFile(file)).map(file => ({ ...file, type: file.type || file.mimeType })),
    ...(images.length ? { images } : {}),
  };
}

function piText(message) {
  return (message?.content || [])
    .filter((c) => c && c.type === 'text' && typeof c.text === 'string')
    .map((c) => c.text)
    .join('\n')
    .trim();
}

function piThinking(message) {
  if (typeof message?.reasoning_content === 'string' && message.reasoning_content.trim()) {
    return message.reasoning_content.trim();
  }
  return (message?.content || [])
    .filter((c) => c && (c.type === 'thinking' || c.type === 'reasoning') && typeof c.text === 'string')
    .map((c) => c.text)
    .join('\n')
    .trim();
}

/** Whether an event can change the transcript row list. */
export function isWorkTranscriptEvent(ev) {
  if (!ev || !WORK_TRANSCRIPT_EVENT_TYPES.has(ev.type)) return false;
  if (ev.type === 'message_end') return !ev.message?.role || ev.message.role === 'assistant';
  return true;
}

/**
 * Keep only what the projection needs, with full text. The narration log
 * (`eventsByTask`) clips message text to a snippet; transcript rows cannot.
 */
export function slimWorkTranscriptEvent(ev) {
  if (!isWorkTranscriptEvent(ev)) return null;
  const seq = Number(ev.seq);
  if (!(seq > 0)) return null;
  const slim = { seq, ts: ev.ts, type: ev.type };
  switch (ev.type) {
    case 'user_message': {
      slim.text = cap(ev.text, USER_TEXT_MAX);
      Object.assign(slim, splitWorkUploadFiles(ev.files, ev.images));
      if (ev.via) slim.via = String(ev.via);
      break;
    }
    case 'message_end': {
      const text = cap(piText(ev.message), ASSISTANT_TEXT_MAX);
      const thinking = cap(piThinking(ev.message), THINKING_MAX);
      slim.text = text;
      if (thinking) slim.thinking = thinking;
      const model = ev.details?.model || ev.message?.model;
      if (model) slim.model = String(model);
      break;
    }
    case 'artifact_ready': {
      const entry = workArtifactEntryFromEvent(ev.artifact || ev);
      if (!entry) return null;
      const fileId = ev.artifact?.file?.id || ev.artifact?.file?._id;
      slim.artifact = fileId ? { ...entry, file: { id: String(fileId) } } : entry;
      break;
    }
    case 'user_question_requested':
      if (typeof ev.required === 'boolean') slim.required = ev.required;
      if (slimWorkMediaReview(ev.mediaReview)) slim.mediaReview = slimWorkMediaReview(ev.mediaReview);
      if (ev.id) slim.id = String(ev.id);
      if (ev.kind) slim.kind = String(ev.kind);
      if (ev.question) slim.question = cap(ev.question, WORK_QUESTION_MAX_CHARS);
      if (Array.isArray(ev.options)) slim.options = ev.options.slice(0, 8);
      if (ev.allowCustom != null) slim.allowCustom = !!ev.allowCustom;
      if (ev.estimatedCredits != null) slim.estimatedCredits = ev.estimatedCredits;
      if (Array.isArray(ev.questions)) {
        slim.questions = ev.questions.slice(0, 6).map((q) => {
          if (!q || typeof q !== 'object') return q;
          return { ...q, question: cap(q.question, WORK_QUESTION_MAX_CHARS) };
        });
      }
      if (ev.plan) slim.plan = ev.plan;
      if (ev.scriptPreview) slim.scriptPreview = cap(ev.scriptPreview, 6000);
      break;
    case 'user_question_resolved':
      if (ev.id) slim.id = String(ev.id);
      if (ev.answer) slim.answer = cap(ev.answer, 2000);
      if (ev.optionId) slim.optionId = String(ev.optionId);
      if (ev.skipped != null) slim.skipped = !!ev.skipped;
      if (ev.dismissed != null) slim.dismissed = !!ev.dismissed;
      if (ev.reason) slim.reason = cap(String(ev.reason), 200);
      if (ev.timedOut != null) slim.timedOut = !!ev.timedOut;
      break;
    case 'session_end':
      if (ev.status) slim.status = String(ev.status);
      if (ev.error) slim.error = cap(ev.error, 2000);
      break;
    default:
      break;
  }
  return slim;
}

/** Insert transcript events into a seq-ordered list, dropping repeats. */
export function mergeWorkTranscriptEvents(existing, incoming) {
  const base = Array.isArray(existing) ? existing : [];
  const fresh = [];
  const seen = new Set(base.map((e) => e.seq));
  for (const ev of Array.isArray(incoming) ? incoming : []) {
    const slim = slimWorkTranscriptEvent(ev);
    if (!slim || seen.has(slim.seq)) continue;
    seen.add(slim.seq);
    fresh.push(slim);
  }
  if (!fresh.length) return base;
  const merged = [...base, ...fresh];
  const lastBase = base.length ? base[base.length - 1].seq : 0;
  const appendOnly = fresh.every((e, i) => e.seq > lastBase && (i === 0 || e.seq > fresh[i - 1].seq));
  if (!appendOnly) merged.sort((a, b) => a.seq - b.seq);
  return merged;
}

function rowSeq(row) {
  return workSeqFromMessageId(row?.id || row?._id);
}

/**
 * Archive pages can overlap history already loaded by a detail refresh. A
 * page's new rows are not necessarily older than every cached row, so batch
 * concatenation is not transcript order. The current task's event sequence
 * is authoritative even when timestamps disagree. Adopted/forked messages
 * remain an inherited prefix; their sequences belong to a different task.
 */
export function orderWorkHistoryMessages(taskId, messages) {
  if (!taskId || !Array.isArray(messages) || messages.length < 2) return messages;
  const prefix = `cw-${taskId}-`;
  const inherited = [];
  const current = [];
  for (const row of messages) {
    const id = row?.id || row?._id;
    const suffix = typeof id === 'string' && id.startsWith(prefix) ? id.slice(prefix.length) : '';
    const event = /^(\d+)(-err)?$/.exec(suffix);
    const holder = /^hold-(\d+)$/.exec(suffix);
    if (event) current.push({ row, seq: Number(event[1]), after: event[2] ? 1 : 0 });
    // Temporary exports belong after their user turn and before its reply.
    else if (holder) current.push({ row, seq: Number(holder[1]), after: 1 });
    else if (suffix === 'streaming') current.push({ row, seq: Infinity, after: 0 });
    else inherited.push(row);
  }
  current.sort((a, b) => a.seq - b.seq || a.after - b.after);
  const ordered = [...inherited, ...current.map(entry => entry.row)];
  return ordered.every((row, index) => row === messages[index]) ? messages : ordered;
}

/**
 * A sync cursor describes the end of a snapshot, not permission to discard
 * its beginning. Only explicit archive metadata establishes a shorter window.
 * Keep a known prefix when an unarchived Work refresh arrives with just a tail.
 * Matching rows remain authoritative (including edits and deletion tombstones).
 */
export function preserveWorkHistoryPrefix({ taskId, serverMessages, previousMessages, archivedMessageCount }) {
  if (archivedMessageCount !== 0 || !taskId || !serverMessages?.length) return serverMessages;
  const officialSeq = row => {
    const seq = rowSeq(row);
    return seq && (row.id || row._id) === workOfficialMessageId(taskId, seq) ? seq : 0;
  };
  const seqs = serverMessages.map(officialSeq);
  // Adopted/forked history and synthetic artifact holders have their own
  // ordering; do not infer a missing prefix from those mixed snapshots.
  if (seqs.some(seq => !seq)) return serverMessages;
  const firstSeq = Math.min(...seqs);
  const prefix = (previousMessages || []).filter(row => {
    const seq = officialSeq(row);
    return !row.isArchivedMessage && seq > 0 && seq < firstSeq;
  });
  return prefix.length ? [...prefix, ...serverMessages] : serverMessages;
}

function lastAssistantText(rows, currentTurnOnly = false) {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i];
    if (currentTurnOnly && row?.role === 'user') return '';
    if (row?.role !== 'assistant') continue;
    if (isWorkArtifactHolderId(row.id)) continue;
    const text = String(row.content || '').trim();
    if (text) return text;
  }
  return '';
}

function currentTurnAssistantIndex(rows) {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i];
    if (row?.role === 'user') return -1;
    if (row?.role === 'assistant' && !row.isStreaming && !row.isTyping) return i;
  }
  return -1;
}

function lastUserSeq(rows) {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i]?.role === 'user') return rowSeq(rows[i]) || 0;
  }
  return 0;
}

function withArtifact(row, artifact) {
  const existing = row?.parameters?.coworkerArtifacts || [];
  const next = { ...artifact, currentTurn: true };
  const idx = existing.findIndex((a) => a?.path === artifact.path);
  if (idx !== -1) {
    const prev = existing[idx];
    if (Number(prev?.size) === Number(next.size) && prev?.mimeType === next.mimeType) return row;
    const list = existing.slice();
    list[idx] = next;
    return {
      ...row,
      parameters: {
        ...(row.parameters || {}),
        coworkerArtifacts: list,
      },
    };
  }
  return {
    ...row,
    parameters: {
      ...(row.parameters || {}),
      coworkerArtifacts: [...existing, next],
    },
  };
}

/**
 * Attach a live deliverable to the current turn's reply, or to a holder row
 * when the turn has not replied yet (same ids as the backend so the synced
 * row replaces it in place).
 */
function artifactBasename(artifactPath) {
  return String(artifactPath || '').split(/[/\\]/).pop();
}

function stripRelocatedArtifacts(rows, artifactPath) {
  if (!artifactPath || !Array.isArray(rows) || !rows.length) return;
  const name = artifactBasename(artifactPath);
  // A changed export can replace a path inside the same user turn too.
  for (let i = 0; i < rows.length; i += 1) {
    const list = rows[i]?.parameters?.coworkerArtifacts;
    if (!Array.isArray(list) || !list.length) continue;
    const next = list.filter((a) => (
      a?.path !== artifactPath
      && (a?.name || artifactBasename(a?.path)) !== name
    ));
    if (next.length === list.length) continue;
    rows[i] = {
      ...rows[i],
      parameters: {
        ...rows[i].parameters,
        coworkerArtifacts: next,
      },
    };
  }
}

function previousTurnStillIndexFromRows(rows) {
  let userIdx = -1;
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i]?.role === 'user') {
      userIdx = i;
      break;
    }
  }
  if (userIdx <= 0) return new Map();
  const artifacts = [];
  for (let i = 0; i < userIdx; i += 1) {
    artifacts.push(...(rows[i]?.parameters?.coworkerArtifacts || []));
  }
  return previousStillSizeIndex(artifacts);
}

function stripRedundantCurrentTurnStills(rows) {
  const index = previousTurnStillIndexFromRows(rows);
  if (!index.size) return;
  let userIdx = -1;
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i]?.role === 'user') {
      userIdx = i;
      break;
    }
  }
  if (userIdx < 0) return;
  for (let i = userIdx + 1; i < rows.length; i += 1) {
    const list = rows[i]?.parameters?.coworkerArtifacts;
    if (!Array.isArray(list) || !list.length) continue;
    const next = list.filter((artifact) => !isRedundantPreviousStill(artifact, index));
    if (next.length === list.length) continue;
    rows[i] = {
      ...rows[i],
      parameters: {
        ...rows[i].parameters,
        coworkerArtifacts: next,
      },
    };
  }
}

function attachArtifact(rows, taskId, artifact, ts) {
  // Replayed exports within an approval/tool loop keep their original bubble.
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i]?.role === 'user') break;
    if (rows[i]?.role === 'assistant' && rows[i].parameters?.coworkerArtifacts?.some((entry) => (
      entry.path === artifact.path && Number(entry.size || 0) === Number(artifact.size || 0)
    ))) return;
  }
  if (isRedundantPreviousStill(artifact, previousTurnStillIndexFromRows(rows))) return;
  stripRelocatedArtifacts(rows, artifact?.path);
  const at = currentTurnAssistantIndex(rows);
  if (at !== -1) {
    const next = withArtifact(rows[at], artifact);
    if (next === rows[at]) return;
    rows[at] = next;
    return;
  }
  const holderId = workArtifactHolderId(taskId, lastUserSeq(rows));
  const holderAt = rows.findIndex((row) => row?.id === holderId);
  if (holderAt !== -1) {
    rows[holderAt] = withArtifact(rows[holderAt], artifact);
    return;
  }
  rows.push(withArtifact({
    id: holderId,
    _stableKey: holderId,
    role: 'assistant',
    content: '',
    timestamp: ts || new Date().toISOString(),
    parameters: { coworkerArtifactHolder: true },
    files: [],
  }, artifact));
}

// A temporary holder may join the first reply that follows it. Once an
// artifact has a real message owner, progress and approval replies must not
// move it again: those replies are not new deliveries.
function absorbArtifactHolders(rows) {
  for (let i = 0; i < rows.length; i += 1) {
    const source = rows[i];
    if (!isWorkArtifactHolderId(source.id)) continue;
    let targetAt = -1;
    for (let j = i + 1; j < rows.length; j += 1) {
      if (rows[j]?.role === 'user') break;
      if (rows[j]?.role === 'assistant' && !isWorkArtifactHolderId(rows[j].id)) {
        targetAt = j;
        break;
      }
    }
    if (targetAt < 0) continue;
    const target = rows[targetAt];
    const byPath = new Map([...(source.parameters?.coworkerArtifacts || []),
      ...(target.parameters?.coworkerArtifacts || [])].map((a) => [a.path, a]));
    const fileKey = (file) => String(file?.id || file?._id || file);
    const files = new Map([...(source.files || []), ...(target.files || [])].map((file) => [fileKey(file), file]));
    rows[targetAt] = {
      ...target,
      files: [...files.values()],
      parameters: { ...target.parameters, coworkerArtifacts: [...byPath.values()] },
    };
    rows[i] = { ...source, files: [], parameters: { ...source.parameters, coworkerArtifacts: [] } };
  }
  return rows.filter((row) => !isWorkArtifactHolderId(row.id)
    || row.parameters?.coworkerArtifacts?.length || row.files?.length);
}

// Exports stay put while work is live. Once a turn closes, its deliverables
// belong below the closing reply, not below an earlier progress update.
// This is a presentation projection so existing saved chats also benefit.
function mentionsDeliveredFile(content, filename) {
  const decode = value => String(value || '').replace(/(?:%[a-f\d]{2})+/gi, part => {
    try { return decodeURIComponent(part); } catch { return part; }
  }).normalize('NFC');
  const name = decode(filename);
  if (!name) return false;
  let matched = false;
  // Compare complete formatted references first. Do not find Quiz 15.docx
  // inside `Old Quiz 15.docx`, or lose spaces when tokenizing **Quiz 15.docx**.
  const text = decode(content).replace(/`([^`\n]+)`|\*\*([^*\n]+)\*\*|__([^\n]+?)__|\[([^\]\n]+)\]\(([^)\n]+)\)|"([^"\n]+)"|“([^”\n]+)”/g,
    (_match, ...groups) => {
      for (const reference of groups.slice(0, 7)) {
        if (reference && artifactBasename(reference) === name) matched = true;
      }
      return ' ';
    });
  if (matched) return true;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // A sentence-ending dot is punctuation; .old, -backup and longer names
  // are different files. A slash may precede a basename in a source path.
  return new RegExp('(?:^|[\\s/\\\\([{>:])' + escaped
    + '(?=$|[\\s)\\]}<,;:!?]|\\.(?=$|\\s))').test(text);
}

export function collectSettledTurnDeliverables(inputRows, settled) {
  const rows = [...inputRows];
  const fileKey = file => String(file?.id || file?._id || file?.path || file?.url || file?.name || file);
  const closeTurn = (start, end) => {
    let targetAt = -1;
    for (let i = end - 1; i >= start; i -= 1) {
      if (rows[i]?.role === 'assistant' && !rows[i].isStreaming && !rows[i].isTyping
        && !isWorkArtifactHolderId(rows[i].id) && String(rows[i].content || '').trim()) { targetAt = i; break; }
    }
    if (targetAt < 0) return;
    const target = rows[targetAt];
    const artifacts = new Map();
    const files = new Map();
    const addFile = file => {
      const key = fileKey(file);
      if (!files.has(key) || (file && typeof file === 'object')) files.set(key, file);
    };
    // A final handoff may explicitly name earlier reviewed source tracks.
    // Include those references without moving their original review cards.
    const named = new Map();
    for (let i = 0; i < start; i += 1) {
      if (rows[i]?.role !== 'assistant') continue;
      for (const artifact of rows[i].parameters?.coworkerArtifacts || []) {
        const name = artifact.name || artifactBasename(artifact.path);
        if (mentionsDeliveredFile(target.content, name)) named.set(name, { artifact, row: rows[i] });
      }
    }
    for (const { artifact, row } of named.values()) {
      artifacts.set(artifact.path, artifact);
      for (const file of row.files || []) {
        if (fileKey(file) === String(artifact.fileId || '') || file?.name === (artifact.name || artifactBasename(artifact.path))) addFile(file);
      }
    }
    for (let i = start; i < end; i += 1) {
      if (rows[i]?.role !== 'assistant') continue;
      for (const artifact of rows[i].parameters?.coworkerArtifacts || []) artifacts.set(artifact.path, artifact);
      for (const file of rows[i].files || []) addFile(file);
    }
    if (!artifacts.size && !files.size) return;
    for (let i = start; i < end; i += 1) {
      const source = rows[i];
      if (source?.role !== 'assistant' || i === targetAt || (!source.files?.length && !source.parameters?.coworkerArtifacts?.length)) continue;
      rows[i] = { ...source, files: [], parameters: { ...source.parameters, coworkerArtifacts: [] } };
    }
    const nextFiles = [...files.values()];
    const nextArtifacts = [...artifacts.values()];
    const unchanged = (previous = [], next) => previous.length === next.length
      && previous.every((item, index) => item === next[index]);
    // Keep memoized server rows stable when their deliveries are already
    // on the closing reply (including repeated projections after sync).
    if (!unchanged(target.files, nextFiles) || !unchanged(target.parameters?.coworkerArtifacts, nextArtifacts)) {
      rows[targetAt] = { ...target, files: nextFiles, parameters: { ...target.parameters, coworkerArtifacts: nextArtifacts } };
    }
  };
  let start = 0;
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i]?.role !== 'user' || rows[i].isPending || rows[i].isQueued) continue;
    if (i > start) closeTurn(start, i);
    start = i + 1;
  }
  if (settled && !rows.slice(start).some(row => row.isStreaming || row.isTyping)) closeTurn(start, rows.length);
  return rows.filter(row => !isWorkArtifactHolderId(row.id) || row.files?.length || row.parameters?.coworkerArtifacts?.length);
}

/**
 * An upgraded chat already painted the opening prompt as a real bubble
 * (Mongo / local id, not `cw-<task>-<seq>`). The launch still emits a
 * user_message with the exec prompt. Drop that one copy — same rule as
 * backend buildMessagesFromEvents `adoptedUserTurn`.
 *
 * Only infer this before the server cursor has moved: after sync the
 * adopted bubble is still a no-seq row, but the launch event is behind
 * `afterSeq` and a later same-text steer must stay visible.
 */
function shouldDropAdoptedLaunchTurn(serverRows, afterSeq, adoptedUserTurn) {
  // After the server cursor has moved, the launch event is behind
  // `afterSeq`. The adopted chat bubble is still a no-seq row, and the
  // in-memory flag can linger if fetchTask omits it — do not treat the
  // next steer as a duplicate launch.
  if (Number(afterSeq) > 0) return false;
  if (adoptedUserTurn === true) return true;
  return (Array.isArray(serverRows) ? serverRows : []).some(
    (row) => row?.role === 'user' && !rowSeq(row),
  );
}

/**
 * Rows for transcript events past the server boundary. Mirrors
 * backend/utils/task_conversation_sync.js buildMessagesFromEvents.
 */
export function projectWorkTail({
  taskId,
  events,
  afterSeq = 0,
  serverRows = [],
  taskPrompt = '',
  adoptedUserTurn = false,
}) {
  // Server rows first, tail rows appended. attachArtifact may replace a
  // server row (a current-turn reply synced before its artifact); the
  // caller gets both halves back.
  const all = [...serverRows];
  const serverCount = serverRows.length;
  let prevAssistant = lastAssistantText(serverRows, true);
  let haveUserTurn = serverRows.some((row) => row?.role === 'user');
  // A loaded history window can contain only assistant rows after its user
  // turn was archived. Only a witnessed launch may use the task's original
  // prompt; absence of a loaded user bubble does not identify a launch.
  let useLaunchPrompt = false;
  let dropAdoptedUserTurn = shouldDropAdoptedLaunchTurn(serverRows, afterSeq, adoptedUserTurn);
  const seenErrorRow = serverRows.some((row) => row?.hasError || row?.flags?.hasError);
  let failed = null;

  for (const ev of Array.isArray(events) ? events : []) {
    const seq = Number(ev?.seq);
    if (!(seq > afterSeq)) continue;
    const id = workOfficialMessageId(taskId, seq);
    const timestamp = ev.ts || new Date().toISOString();
    if (ev.type === 'session_start') {
      useLaunchPrompt = !haveUserTurn && afterSeq === 0 && serverCount === 0;
    } else if (ev.type === 'session_resume') {
      useLaunchPrompt = false;
    }
    if (ev.type === 'user_message') {
      prevAssistant = '';
      if (dropAdoptedUserTurn) {
        dropAdoptedUserTurn = false;
        haveUserTurn = true;
        continue;
      }
      const raw = useLaunchPrompt && !haveUserTurn && taskPrompt ? taskPrompt : String(ev.text || '');
      useLaunchPrompt = false;
      haveUserTurn = true;
      // Never fall back to the raw exec prompt — stripping tags-but-keeping
      // children is how copy used to leak credits/catalog text.
      const text = stripWorkInjectedPrompt(raw);
      const uploads = splitWorkUploadFiles(ev.files, ev.images);
      if (!text && !uploads.files.length && !uploads.images?.length) continue;
      all.push({
        id,
        _stableKey: id,
        role: 'user',
        ...(ev.changeSetId ? { parameters: { workChangeSetId: ev.changeSetId } } : {}),
        content: cap(text, USER_TEXT_MAX),
        timestamp,
        ...uploads,
      });
    } else if (ev.type === 'message_end') {
      const text = String(ev.text || '');
      const thinking = String(ev.thinking || '');
      if (!text && !thinking) continue;
      // Resume / compact can replay the previous reply under a new seq.
      if (text && text === prevAssistant) continue;
      all.push({
        id,
        _stableKey: id,
        role: 'assistant',
        ...(ev.changeSetId ? { parameters: { workChangeSetId: ev.changeSetId } } : {}),
        content: text,
        thinkingContent: thinking,
        timestamp,
        files: [],
        ...(ev.model ? { modelIds: [ev.model] } : {}),
      });
      if (text) prevAssistant = text;
    } else if (ev.type === 'artifact_ready' && ev.artifact) {
      attachArtifact(all, taskId, ev.artifact, timestamp);
    } else if (ev.type === 'session_end') {
      if (ev.status === 'failed' && ev.error) failed = { seq, error: String(ev.error), ts: timestamp };
    }
  }

  if (failed && !seenErrorRow) {
    const id = `${workOfficialMessageId(taskId, failed.seq)}-err`;
    all.push({
      id,
      _stableKey: id,
      role: 'assistant',
      content: `The task run failed: ${failed.error}`,
      timestamp: failed.ts,
      hasError: true,
      flags: { hasError: true },
      files: [],
    });
  }

  stripRedundantCurrentTurnStills(all);
  return { rows: all.slice(serverCount), replacedServerRows: all.slice(0, serverCount) };
}

/** Shared by the projection and store so a hidden placeholder is retired too. */
export function workPendingSendIsSettled(taskId, send, { messages = [], events = [] } = {}) {
  const want = stripWorkInjectedPrompt(send?.text);
  const after = Number(send?.afterSeq) || 0;
  const isLaunch = send?.id === `launch-${taskId}`;
  // The launch event carries execution context (including Mac access
  // instructions), not necessarily the exact composer text. Its identity is
  // already known. Any accepted user turn acknowledges that launch, even if
  // the opening event is outside a reconnect's retained window.
  for (const ev of events) {
    if (ev?.type !== 'user_message' || !(Number(ev.seq) > after)) continue;
    if (isLaunch || stripWorkInjectedPrompt(ev.text) === want) return true;
  }
  for (const row of messages) {
    const seq = rowSeq(row) || 0;
    if (seq <= after) continue;
    if ((row.id || row._id) !== workOfficialMessageId(taskId, seq)) continue;
    // A saved assistant reply also proves execution accepted the launch.
    // Its user row may already have been archived by conversation sync.
    if (isLaunch && (row.role === 'user' || row.role === 'assistant')) return true;
    if (row.role === 'user' && stripWorkInjectedPrompt(row.content) === want) return true;
  }
  return false;
}

function pendingSendRow(send) {
  return {
    id: send.id,
    _stableKey: send.id,
    role: 'user',
    content: String(send.text || ''),
    ...splitWorkUploadFiles(send.files, send.images),
    timestamp: new Date(send.createdAt || Date.now()).toISOString(),
    isPending: true,
  };
}

/**
 * The live draft row. Hidden while it is still a prefix of the previous
 * reply (a replay in progress) and while it has no visible text.
 */
function liveDraftRow(taskId, draft, rows) {
  if (!draft) return null;
  const content = String(draft.paintedContent ?? draft.content ?? '');
  const thinking = String(draft.paintedThinking ?? draft.thinking ?? '');
  const officialId = draft.officialSeq ? workOfficialMessageId(taskId, draft.officialSeq) : null;
  if (officialId) {
    // message_end landed and the official row exists: keep revealing the
    // paced text on that row instead of a second bubble.
    const at = rows.findIndex((row) => row?.id === officialId);
    if (at === -1) return null;
    const official = rows[at];
    if (content.length >= String(official.content || '').length) return null;
    return {
      index: at,
      row: {
        ...official,
        content,
        thinkingContent: thinking || official.thinkingContent,
        isStreaming: true,
        isTyping: true,
      },
    };
  }
  const prev = lastAssistantText(rows);
  const trimmed = content.trim();
  if (trimmed && prev && prev.length >= trimmed.length && prev.startsWith(trimmed)) return null;
  const id = workStreamingMessageId(taskId);
  return {
    index: -1,
    row: {
      id,
      _stableKey: id,
      role: 'assistant',
      content,
      thinkingContent: thinking,
      timestamp: new Date(draft.startedAt || Date.now()).toISOString(),
      isStreaming: true,
      isTyping: true,
      isThinking: !!draft.isThinking && !trimmed,
      files: [],
    },
  };
}

/**
 * @param {Object} input
 * @param {string} input.taskId
 * @param {Array} input.serverMessages  rows from Conversation.messages
 * @param {number} input.syncedSeq      eventsSyncedSeq reported by the detail payload
 * @param {number} input.archivedMessageCount explicit history-window metadata, when available
 * @param {Array} input.transcriptEvents slim transcript events (seq ascending)
 * @param {Object|null} input.liveDraft
 * @param {Array} input.pendingSends
 * @param {string} input.taskPrompt
 * @param {Object} input.launchQueue  durable initial submission receipt, including its timestamp
 * @param {boolean} input.adoptedUserTurn  chat → Work upgrade already has the opening bubble
 * @returns {Array} rows
 */
export function projectWorkTranscript({
  taskId,
  serverMessages = [],
  syncedSeq = 0,
  archivedMessageCount,
  transcriptEvents = [],
  liveDraft = null,
  pendingSends = [],
  taskPrompt = '',
  launchQueue = null,
  adoptedUserTurn = false,
  settled = false,
}) {
  let serverRows = orderWorkHistoryMessages(taskId, Array.isArray(serverMessages) ? serverMessages : []);
  if (archivedMessageCount === 0 && serverRows.length && transcriptEvents.length) {
    const firstSeq = Math.min(...serverRows.map(row => rowSeq(row) || 0));
    if (firstSeq > 0) {
      // The opening may still exist only in the live event log when a partial
      // detail refresh lands. Preserve it before applying the saved cursor.
      const prefix = projectWorkTail({ taskId, taskPrompt, adoptedUserTurn,
        events: transcriptEvents.filter(event => Number(event.seq) < firstSeq) }).rows;
      serverRows = preserveWorkHistoryPrefix({ taskId, serverMessages: serverRows,
        previousMessages: prefix, archivedMessageCount });
    }
  }
  // Rows the server already materialized end at the greater of the
  // reported cursor and the highest seq we can see in the rows themselves
  // (a detail payload from before `workSync` existed still has ids).
  // A sidebar summary can carry workSync.eventsSyncedSeq with no messages —
  // that cursor is meaningless until the rows exist, so ignore it.
  const boundary = workTranscriptSinceSeq(serverRows, syncedSeq);

  const { rows: tailRows, replacedServerRows } = projectWorkTail({
    taskId,
    events: transcriptEvents,
    afterSeq: boundary,
    serverRows,
    taskPrompt,
    adoptedUserTurn,
  });

  const rows = absorbArtifactHolders([...replacedServerRows, ...tailRows]);

  const live = [];
  const draft = liveDraftRow(taskId, liveDraft, rows);
  if (draft && draft.index !== -1) {
    rows[draft.index] = draft.row;
  } else if (draft) {
    live.push(draft.row);
  }
  // Mongo acknowledges the user's request before execution has a transcript.
  // Keep that saved request visible even if Stop prevents execution entirely.
  // Prefer real history once available, including when the initial user row
  // was archived and only later execution rows remain.
  const launchId = `launch-${taskId}`;
  const sends = Array.isArray(pendingSends) ? pendingSends : [];
  const optimisticLaunch = sends.find(send => send?.id === launchId);
  const savedLaunch = launchQueue?.version === 1 && !adoptedUserTurn && taskPrompt
    ? { ...optimisticLaunch, id: launchId, text: taskPrompt, afterSeq: 0,
      createdAt: launchQueue.submittedAt || optimisticLaunch?.createdAt }
    : null;
  if (savedLaunch && !rows.some(row => row.role === 'user')
    && !workPendingSendIsSettled(taskId, savedLaunch, { messages: rows, events: transcriptEvents })) {
    live.push({ ...pendingSendRow(savedLaunch), isPending: false });
  }
  for (const send of sends) {
    if (savedLaunch && send?.id === launchId) continue;
    if (!send?.id || workPendingSendIsSettled(taskId, send, { messages: rows, events: transcriptEvents })) continue;
    live.push(pendingSendRow(send));
  }
  // A send typed while a reply streams sits under the reply it interrupts;
  // a reply that starts after the send sits under the send.
  live.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  rows.push(...live);
  // Queued follow-ups are not transcript rows; WorkFollowUpQueue is the one
  // place they render until they are actually sent.
  const lastEnd = [...transcriptEvents].reverse().find(event => event.type === 'session_end');
  const ended = lastEnd && Number(lastEnd.seq) >= Math.max(0, ...rows.map(row => rowSeq(row) || 0));
  return collectSettledTurnDeliverables(rows, settled || !!ended);
}

const ROW_COMPARE_KEYS = [
  'content', 'thinkingContent', 'role', 'timestamp', 'isStreaming', 'isTyping', 'isThinking',
  'isPending', 'isQueued', 'hasError', 'files', 'images', 'parameters', 'modelIds', 'reactions',
];

function sameRow(a, b) {
  if (a === b) return true;
  if (!a || !b || a.id !== b.id) return false;
  for (const key of ROW_COMPARE_KEYS) {
    const va = a[key];
    const vb = b[key];
    if (va === vb) continue;
    if (Array.isArray(va) && Array.isArray(vb)) {
      if (va.length !== vb.length) return false;
      for (let i = 0; i < va.length; i += 1) {
        if (va[i] !== vb[i] && JSON.stringify(va[i]) !== JSON.stringify(vb[i])) return false;
      }
      continue;
    }
    if (va && vb && typeof va === 'object' && typeof vb === 'object') {
      if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
      continue;
    }
    return false;
  }
  return true;
}

/**
 * Keep row object identity across projections when nothing in the row
 * changed, so memoized renderers and FlatList item comparisons stay cheap.
 */
export function createWorkTranscriptMemo() {
  let prevRows = [];
  let prevById = new Map();
  return function memoize(rows) {
    const list = Array.isArray(rows) ? rows : [];
    const nextById = new Map();
    let changed = list.length !== prevRows.length;
    const out = new Array(list.length);
    for (let i = 0; i < list.length; i += 1) {
      const row = list[i];
      const prev = prevById.get(row?.id);
      const keep = prev && sameRow(prev, row) ? prev : row;
      out[i] = keep;
      if (row?.id) nextById.set(row.id, keep);
      if (!changed && prevRows[i] !== keep) changed = true;
    }
    if (!changed) return prevRows;
    prevRows = out;
    prevById = nextById;
    return out;
  };
}

/**
 * The seq the client needs transcript events from: everything after the
 * server boundary. Used by the open path (`events.json?kinds=transcript`).
 */
export function workTranscriptSinceSeq(serverMessages, syncedSeq) {
  const rows = Array.isArray(serverMessages) ? serverMessages : [];
  let fromRows = 0;
  for (const row of rows) {
    const seq = rowSeq(row);
    if (seq && seq > fromRows) fromRows = seq;
  }
  const reported = Number(syncedSeq) || 0;
  // List stubs set eventsSyncedSeq without shipping messages. Using that
  // cursor would skip the entire transcript tail and render a blank thread.
  if (rows.length === 0) return 0;
  return reported > fromRows ? reported : fromRows;
}
